import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ExternalLink, Link2, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CustomerPicker } from '@/components/customer-picker';
import { duration, EventDialog, SEVERITIES, SeverityBadge } from '@/components/monitoring';
import { Tabs } from '@/components/tickets';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Alert, Badge, Card, Input, Select, Skeleton } from '@/components/ui/primitives';
import { EmptyRow, Pagination, Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { AutoTicketMode, DeviceItem, MonitoredGroupItem, MonitoredHostItem, MonitoringEventItem, MonitoringOverview, Paginated, Ref } from '@/lib/types';
import { cn, formatDateTime, formatRelative } from '@/lib/utils';

const onError = (e: unknown) => toast.error(errorMessage(e));

function useDebounced(value: string, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function Overview({ data, minSeverity, onSeverity }: { data: MonitoringOverview; minSeverity: number; onSeverity: (s: number) => void }) {
  const broken = data.instances.filter((i) => i.active && i.status === 'ERROR');
  return (
    <div className="mb-6 grid gap-4">
      {data.instances.length === 0 && (
        <Alert>
          Noch keine Monitoring-Quelle (Zabbix oder NetDoc) verbunden.{' '}
          <Link to="/admin/integrations/monitoring" className="font-medium text-primary hover:underline">
            Jetzt einrichten
          </Link>
        </Alert>
      )}
      {broken.map((i) => (
        <Alert key={i.id} variant="destructive">
          <span className="font-medium">{i.name}:</span> {i.lastError ?? 'Verbindung gestört'}
        </Alert>
      ))}
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        {[...SEVERITIES.keys()].reverse().map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onSeverity(minSeverity === s ? 0 : s)}
            aria-pressed={minSeverity === s}
            className={cn('rounded-lg border bg-card p-3 text-left transition-shadow hover:shadow-sm', minSeverity === s && 'ring-2 ring-primary')}
          >
            <span className="mb-1 block h-1.5 rounded-full" style={{ background: SEVERITIES[s]!.color }} />
            <span className="block text-2xl font-semibold tabular-nums">{data.bySeverity[s]}</span>
            <span className="block text-xs text-muted-foreground">{SEVERITIES[s]!.label}</span>
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        {data.hosts} überwachte Hosts{data.unassignedHosts > 0 && `, davon ${data.unassignedHosts} ohne direkte Kundenzuordnung`} ·{' '}
        {data.instances
          .map((i) => `${i.name}: ${i.kind === 'ZABBIX' ? (i.lastWebhookAt ? `Webhook ${formatRelative(i.lastWebhookAt)}` : 'noch kein Webhook') + ', ' : ''}${i.lastPollAt ? `Abgleich ${formatRelative(i.lastPollAt)}` : 'noch kein Abgleich'}`)
          .join(' · ')}
      </p>
    </div>
  );
}

function EventsTable({ status, minSeverity }: { status: 'PROBLEM' | 'RESOLVED' | undefined; minSeverity: number }) {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const dq = useDebounced(q);
  const [open, setOpen] = useState<string | null>(null);
  useEffect(() => setPage(1), [status, minSeverity, dq]);
  const params = new URLSearchParams({ page: String(page), pageSize: '25', ...(status && { status }), ...(minSeverity > 0 && { minSeverity: String(minSeverity) }), ...(dq && { q: dq }) });
  const { data } = useQuery({
    queryKey: ['monitoring', 'events', params.toString()],
    queryFn: () => api<Paginated<MonitoringEventItem>>(`/monitoring/events?${params}`),
    refetchInterval: status === 'PROBLEM' ? 30_000 : false,
  });
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2 border-b p-3">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input className="pl-8" placeholder="Problem oder Host suchen" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Suche" />
        </div>
        {minSeverity > 0 && <Badge variant="outline">ab {SEVERITIES[minSeverity]!.label}</Badge>}
      </div>
      <Table>
        <THead>
          <TR className="hover:bg-transparent">
            <TH>Schweregrad</TH>
            <TH>Problem</TH>
            <TH className="hidden md:table-cell">Kunde</TH>
            <TH>{status === 'RESOLVED' ? 'Zeitraum' : 'Seit'}</TH>
            <TH>Ticket</TH>
          </TR>
        </THead>
        <TBody>
          {data?.items.length === 0 && <EmptyRow colSpan={5}>{status === 'PROBLEM' ? 'Keine aktiven Probleme.' : 'Keine Ereignisse.'}</EmptyRow>}
          {data?.items.map((e) => (
            <TR key={e.id} className="cursor-pointer" onClick={() => setOpen(e.id)}>
              <TD>
                <SeverityBadge severity={e.severity} />
              </TD>
              <TD>
                <p className="font-medium">{e.name}</p>
                <p className="text-xs text-muted-foreground">
                  {e.host?.name ?? 'Unbekannter Host'} · {e.instance.name}
                  {e.acknowledged && ' · bestätigt'}
                  {e.status === 'RESOLVED' && ' · behoben'}
                </p>
                {e.occurrences > 1 && (
                  <p className="text-xs text-amber-700 dark:text-amber-300" title={e.baseUid ?? undefined}>
                    {e.occurrences}-mal aufgetreten seit {formatDateTime(e.firstOccurredAt)}
                  </p>
                )}
              </TD>
              <TD className="hidden text-sm md:table-cell">{e.customer?.name ?? <span className="text-muted-foreground">–</span>}</TD>
              <TD className="whitespace-nowrap text-sm">
                {status === 'RESOLVED' ? `${formatDateTime(e.startedAt)} (${duration(e.startedAt, e.resolvedAt)})` : duration(e.startedAt, e.resolvedAt)}
              </TD>
              <TD className="text-sm">
                {e.ticket ? (
                  <Link to={`/tickets/${e.ticket.id}`} className="text-primary hover:underline" onClick={(ev) => ev.stopPropagation()}>
                    {e.ticket.number}
                  </Link>
                ) : (
                  <span className="text-xs text-muted-foreground" title={e.ticketDecision ?? undefined}>
                    kein Ticket
                  </span>
                )}
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
      {data && <Pagination page={page} pageSize={25} total={data.total} onPage={setPage} />}
      <EventDialog eventId={open} onClose={() => setOpen(null)} />
    </Card>
  );
}

const AUTO_LABELS: Record<AutoTicketMode, string> = { INHERIT: 'wie Instanz', ALWAYS: 'immer', NEVER: 'nie' };

function HostDialog({ host, onClose }: { host: MonitoredHostItem | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [customer, setCustomer] = useState<Ref | null>(null);
  const [deviceId, setDeviceId] = useState<string>('');
  const [mode, setMode] = useState<AutoTicketMode>('INHERIT');
  useEffect(() => {
    if (host) {
      setCustomer(host.customer);
      setDeviceId(host.device?.id ?? '');
      setMode(host.autoTicket);
    }
  }, [host]);
  const { data: devices } = useQuery({
    queryKey: ['devices', 'for-host', customer?.id ?? 'all'],
    queryFn: () => api<Paginated<DeviceItem>>(`/devices?pageSize=100${customer ? `&customerId=${customer.id}` : ''}`),
    enabled: host !== null,
  });
  const save = useMutation({
    mutationFn: () => api(`/monitoring/hosts/${host!.id}`, { method: 'PATCH', body: { customerId: customer?.id ?? null, deviceId: deviceId || null, autoTicket: mode } }),
    onSuccess: () => {
      toast.success('Host gespeichert');
      qc.invalidateQueries({ queryKey: ['monitoring'] });
      onClose();
    },
    onError,
  });
  return (
    <Dialog open={host !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        {host && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate();
            }}
          >
            <DialogHeader>
              <DialogTitle>{host.name}</DialogTitle>
              <DialogDescription>
                {host.host} · {host.instance.name}
                {host.groups.length > 0 && ` · ${host.groups.join(', ')}`}
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="grid gap-4">
              <Field
                label="Kunde"
                hint={host.groupCustomer && !customer ? `Ohne Auswahl gilt der Kunde der Hostgruppe: ${host.groupCustomer.name}` : 'Leer lassen, um den Kunden über die Hostgruppe zu bestimmen.'}
              >
                <CustomerPicker value={customer} onChange={(c) => (setCustomer(c), setDeviceId(''))} />
              </Field>
              <Field label="Verknüpftes Gerät" htmlFor="h-dev" hint="Verknüpft den Zabbix-Host mit einem Gerät der Geräteverwaltung.">
                <Select id="h-dev" value={deviceId} onChange={(e) => setDeviceId(e.target.value)}>
                  <option value="">– kein Gerät –</option>
                  {devices?.items.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.manufacturer} {d.model}
                      {d.serialNumber ? ` (${d.serialNumber})` : ''}
                      {!customer && d.customer ? ` – ${d.customer.name}` : ''}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Automatische Tickets" htmlFor="h-auto">
                <Select id="h-auto" value={mode} onChange={(e) => setMode(e.target.value as AutoTicketMode)}>
                  <option value="INHERIT">Wie in der Zabbix-Instanz eingestellt</option>
                  <option value="ALWAYS">Immer (auch wenn für die Instanz abgeschaltet)</option>
                  <option value="NEVER">Nie für diesen Host</option>
                </Select>
              </Field>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Abbrechen
              </Button>
              <Button type="submit" loading={save.isPending}>
                Speichern
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function HostsTable() {
  const { can } = useAuth();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<'' | 'unassigned' | 'problems'>('');
  const dq = useDebounced(q);
  const [edit, setEdit] = useState<MonitoredHostItem | null>(null);
  useEffect(() => setPage(1), [dq, filter]);
  const params = new URLSearchParams({
    page: String(page),
    pageSize: '50',
    ...(dq && { q: dq }),
    ...(filter === 'unassigned' && { unassigned: 'true' }),
    ...(filter === 'problems' && { withProblems: 'true' }),
  });
  const { data } = useQuery({ queryKey: ['monitoring', 'hosts', params.toString()], queryFn: () => api<Paginated<MonitoredHostItem>>(`/monitoring/hosts?${params}`) });
  return (
    <Card>
      <div className="flex flex-wrap gap-2 border-b p-3">
        <Input className="w-full sm:w-64" placeholder="Host suchen" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Host suchen" />
        <Select className="w-auto" value={filter} onChange={(e) => setFilter(e.target.value as typeof filter)} aria-label="Filter">
          <option value="">Alle Hosts</option>
          <option value="problems">Mit aktiven Problemen</option>
          <option value="unassigned">Ohne direkte Kundenzuordnung</option>
        </Select>
      </div>
      <Table>
        <THead>
          <TR className="hover:bg-transparent">
            <TH>Host</TH>
            <TH className="hidden md:table-cell">Gruppen</TH>
            <TH>Kunde</TH>
            <TH className="hidden lg:table-cell">Gerät</TH>
            <TH>Probleme</TH>
            <TH className="hidden md:table-cell">Tickets</TH>
          </TR>
        </THead>
        <TBody>
          {data?.items.length === 0 && <EmptyRow colSpan={6}>Keine Hosts. Hosts werden in der Zabbix-Konfiguration importiert.</EmptyRow>}
          {data?.items.map((h) => (
            <TR key={h.id} className={cn(can('devices.manage') && 'cursor-pointer')} onClick={() => can('devices.manage') && setEdit(h)}>
              <TD>
                <p className="flex flex-wrap items-center gap-1.5 font-medium">
                  {h.name} {!h.enabled && <Badge variant="outline">deaktiviert</Badge>}
                  {h.onlineStatus && <Badge variant={h.onlineStatus === 'online' ? 'success' : 'secondary'}>{h.onlineStatus === 'online' ? 'online' : 'offline'}</Badge>}
                  {h.externalUrl && (
                    <a href={h.externalUrl} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground" onClick={(e) => e.stopPropagation()} aria-label="In NetDoc öffnen">
                      <ExternalLink className="size-3.5" />
                    </a>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  {h.host} · {h.instance.name}
                </p>
              </TD>
              <TD className="hidden max-w-[14rem] truncate text-xs text-muted-foreground md:table-cell">{h.groups.join(', ') || '–'}</TD>
              <TD className="text-sm">
                {h.customer?.name ?? (h.groupCustomer ? <span className="text-muted-foreground">{h.groupCustomer.name} (Gruppe)</span> : <span className="text-muted-foreground">–</span>)}
              </TD>
              <TD className="hidden text-sm lg:table-cell">
                {h.device ? (
                  <Link to={`/devices/${h.device.id}`} className="inline-flex items-center gap-1 hover:underline" onClick={(e) => e.stopPropagation()}>
                    <Link2 className="size-3" /> {h.device.name}
                  </Link>
                ) : (
                  <span className="text-muted-foreground">–</span>
                )}
              </TD>
              <TD>{h.openProblems > 0 && h.maxSeverity !== null ? <SeverityBadge severity={h.maxSeverity} className="tabular-nums" /> : <span className="text-sm text-muted-foreground">ok</span>}{h.openProblems > 1 && <span className="ml-1 text-xs text-muted-foreground">×{h.openProblems}</span>}</TD>
              <TD className="hidden text-sm text-muted-foreground md:table-cell">{AUTO_LABELS[h.autoTicket]}</TD>
            </TR>
          ))}
        </TBody>
      </Table>
      {data && <Pagination page={page} pageSize={50} total={data.total} onPage={setPage} />}
      <HostDialog host={edit} onClose={() => setEdit(null)} />
    </Card>
  );
}

function GroupsTable() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const { data } = useQuery({ queryKey: ['monitoring', 'groups'], queryFn: () => api<{ items: MonitoredGroupItem[] }>('/monitoring/groups') });
  const save = useMutation({
    mutationFn: ({ id, customer }: { id: string; customer: Ref | null }) =>
      api<{ ok: boolean; warning?: string }>(`/monitoring/groups/${id}`, { method: 'PATCH', body: { customerId: customer?.id ?? null } }),
    onSuccess: (r) => {
      if (r.warning) toast.warning(r.warning, { duration: 10_000 });
      else toast.success('Zuordnung gespeichert');
      qc.invalidateQueries({ queryKey: ['monitoring'] });
    },
    onError,
  });
  if (!data) return <Skeleton className="h-40" />;
  return (
    <Card>
      <p className="border-b p-3 text-sm text-muted-foreground">
        Ein Kunde an einer Zabbix-Hostgruppe bzw. einem NetDoc-Mandanten gilt für alle zugehörigen Hosts und Endpunkte ohne eigene Zuordnung. NetDoc-Mandanten mit der Kundennummer als <code>external_id</code> werden beim Import automatisch zugeordnet.
      </p>
      <Table>
        <THead>
          <TR className="hover:bg-transparent">
            <TH>Hostgruppe / NetDoc-Mandant</TH>
            <TH className="w-20">Hosts</TH>
            <TH className="w-80">Kunde</TH>
          </TR>
        </THead>
        <TBody>
          {data.items.length === 0 && <EmptyRow colSpan={3}>Keine Hostgruppen importiert.</EmptyRow>}
          {data.items.map((g) => (
            <TR key={g.id}>
              <TD>
                <p className="font-medium">{g.name}</p>
                <p className="text-xs text-muted-foreground">
                  {g.instance.name}
                  {g.externalId && ` · external_id ${g.externalId}`}
                </p>
              </TD>
              <TD className="tabular-nums">{g.hostCount}</TD>
              <TD>{can('devices.manage') ? <CustomerPicker value={g.customer} onChange={(c) => save.mutate({ id: g.id, customer: c })} /> : g.customer?.name ?? '–'}</TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </Card>
  );
}

export function MonitoringPage() {
  const [tab, setTab] = useState<'problems' | 'hosts' | 'groups' | 'history'>('problems');
  const [minSeverity, setMinSeverity] = useState(0);
  const { data } = useQuery({ queryKey: ['monitoring', 'overview'], queryFn: () => api<MonitoringOverview>('/monitoring/overview'), refetchInterval: 30_000 });
  return (
    <div>
      <PageHeader title="Monitoring" description="Probleme und Alarme aus Zabbix und NetDoc, überwachte Hosts und Endpunkte sowie ihre Zuordnung zu Kunden und Geräten." />
      {data ? <Overview data={data} minSeverity={minSeverity} onSeverity={(s) => (setMinSeverity(s), setTab('problems'))} /> : <Skeleton className="mb-6 h-28" />}
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          {
            value: 'problems',
            label: (
              <span className="inline-flex items-center gap-1.5">
                <AlertTriangle className="size-3.5" /> Aktive Probleme {data ? `(${data.openProblems})` : ''}
              </span>
            ),
          },
          { value: 'hosts', label: 'Hosts' },
          { value: 'groups', label: 'Gruppen / Mandanten' },
          { value: 'history', label: 'Verlauf' },
        ]}
      />
      <div className="mt-4">
        {tab === 'problems' && <EventsTable status="PROBLEM" minSeverity={minSeverity} />}
        {tab === 'history' && <EventsTable status={undefined} minSeverity={minSeverity} />}
        {tab === 'hosts' && <HostsTable />}
        {tab === 'groups' && <GroupsTable />}
      </div>
    </div>
  );
}
